// Copyright (c) 2025 UXtechLab. All Rights Reserved.
// Originally created by @taekie. Licensed under BUSL-1.1 by UXtechLab. See LICENSE for details.
/**
 * VoronoiTreemap
 *
 * Main class for creating voronoi treemap visualizations.
 * Renders hierarchical data as organic, pebble-like cells using
 * D3.js and voronoi treemap algorithms.
 *
 * Features:
 * - Hierarchical voronoi treemap layout
 * - Customizable colors, labels, and sizing
 * - Interactive click and hover events
 * - Pebble-style rounded outlines
 * - Label collision detection and adjustment
 * - Region position control for deterministic layouts
 */

import d3 from './utils/d3-bundle.js';
import { PebbleRenderer } from './PebbleRenderer.js';
import { LabelAdjuster } from './LabelAdjuster.js';
import { nestingForVoronoi } from './nestingForVoronoi.js';
import VoronoiTreemapHelpers from './VoronoiTreemapHelpers.js';
import { getBubbleStyles } from './PopupHelpers.js';

/**
 * VoronoiTreemap - Main visualization class
 *
 * @example
 * const treemap = new VoronoiTreemap();
 * const svg = treemap.render(data, {
 *   width: 800,
 *   height: 600,
 *   maptitle: 'My Treemap'
 * });
 * document.body.appendChild(svg);
 */
class VoronoiTreemap {
  constructor() {
    this.margin = { top: 50, right: 50, bottom: 50, left: 50 };
    this.svg = null;
    this.data = null;
    this.hierarchy = null;
    this.allNodes = null;
    this._pebbleRenderer = null;
    this._labelAdjuster = null;
  }

  // === Default Color Palette ===
  static get DEFAULT_COLORS() {
    return "#afc7dd,#ffe9a9,#f69f8f,#b4c8af,#e9e4d6,#bed1d8,#f8dba1,#fcbc8b,#d7e0c4,#c5b5a6,#b5ccc1,#e9bfb4,#e9f0f6,#fffefb,#fce0db,#e1e9df,#f1f5f7,#fef8ed,#feeada,#fbfcf9,#e5ded7,#e5edea,#fbf5f3,#96b6d3,#ffdf85,#f3836e,#a0b99a,#ddd4be,#a7c1cb,#f5cf80,#fba868,#c7d4ac,#b7a490,#a0bdb0,#e1a799,#d7e3ee,#fff7e1,#facbc3,#d3dfd0,#fdfcfa,#e1eaed,#fcefd5,#fddcc2,#f0f3e9,#dbd2c8,#d6e3dd,#f6e4df,#dee8f1,#fffaeb,#fbd4cc,#d9e3d6,#e7eef1,#fcf3df,#fee1cc,#f4f7ef,#dfd7ce,#dce7e2,#f8ebe7,#e5edf4,#fffdf5,#fcdcd6,#dfe7dc,#eef3f5,#fdf6e8,#fee7d6,#f9faf6,#e3dcd4,#e2ebe7,#faf1ef,#d0b7ba,#b8cec4,#d2b6b6,#b6bdd6,#d9b8b7,#ded5b6,#bac2d7,#c8d5be,#e3bfb7,#f9dfb3,#eac2b8,#c1d3da,#ddc7c1,#d9e2c7,#cfdad5,#eecdc1,#ccdddf,#c7d7e6,#ded6cf,#e7d1cb,#ced9e5,#eedbc8,#d7e3e2,#e3ead2,#ecdcd2,#d9e0e5,#efe1d2,#ebdad7,#eed6da,#e1e6de,#dde4e8,#eee1d8,#f5e8d7,#f1e6dd,#f5e8de,#f3e7e1,#f5eee1,#f5f2ec".split(
      ","
    );
  }

  // === Default Options ===
  static get DEFAULT_OPTIONS() {
    return {
      width: 1200,
      height: 900,
      maptitle: "",
      caption: "",
      clickFunc: () => {},
      colorFunc: null,
      sentiment: null, // 'fieldName' | { field, domain:[lo,hi] } — built-in diverging sentiment colormap
      getCellColors: null, // (cellColors) => void - Callback to receive actual cell colors
      sizeLimit: 1000,
      ratioLimit: 0,
      pieSize: 1,
      colors: VoronoiTreemap.DEFAULT_COLORS,
      seedRandom: 10,
      showMetaLabel: false,
      showPercent: false,
      underLabel: false,
      positions: null,
      forceNodeFunc: null,
      debug: false,
      pebbleRound: 25,
      pebbleWidth: 5,
      keyColors: [],
      // Custom HTML label renderers — one per depth.
      // (datum, defaultHtml, ctx) => HTML string. ctx.depth is 1 (group) or 2 (subgroup).
      renderGroupLabel: null,    // depth 1 (group)
      renderSubgroupLabel: null, // depth 2 (subgroup)
      adaptiveIterations: true,
      cellImage: null, // (datum) => { url, mode: 'fill'|'fit', opacity: 0~1, colorMode: 'original'|'tint' } | null
      labelMode: 'show', // 'show' | 'faded' | 'hidden'
      levels: ['metaLabel', 'label', 'text'], // field names per depth (0, 1, 2)
      value: 'bubbleSize' // field name for size weight
    };
  }

  // === Getter for post-processing modules ===
  get pebbleRenderer() {
    if (!this._pebbleRenderer) {
      this._pebbleRenderer = new PebbleRenderer(d3);
    }
    return this._pebbleRenderer;
  }

  get labelAdjuster() {
    if (!this._labelAdjuster) {
      this._labelAdjuster = new LabelAdjuster(d3);
    }
    return this._labelAdjuster;
  }

  // === Public Methods ===

  /**
   * Render chart - returns SVG element
   * @param {Object[]} data - Data array to visualize
   * @param {Object} [options] - Rendering options
   * @returns {SVGSVGElement} - Generated SVG element
   */
  render(data, options = {}) {
    // Legacy option name support
    const normalizedOptions = { ...options };
    if ('showRegion' in normalizedOptions && !('showMetaLabel' in normalizedOptions))
      normalizedOptions.showMetaLabel = normalizedOptions.showRegion;
    if (!('positions' in normalizedOptions)) {
      if ('metaLabelPositions' in normalizedOptions) normalizedOptions.positions = normalizedOptions.metaLabelPositions;
      else if ('regionPositions' in normalizedOptions) normalizedOptions.positions = normalizedOptions.regionPositions;
    }
    if (!('keyColors' in normalizedOptions)) {
      if ('metaLabelColors' in normalizedOptions) normalizedOptions.keyColors = normalizedOptions.metaLabelColors;
      else if ('regionColors' in normalizedOptions) normalizedOptions.keyColors = normalizedOptions.regionColors;
    }
    // Canonical renderers: renderGroupLabel (depth 1) / renderSubgroupLabel (depth 2)
    // Legacy aliases → canonical (explicit canonical option always wins)
    if (!('renderGroupLabel' in normalizedOptions)) {
      if ('metaLabelRenderer' in normalizedOptions) normalizedOptions.renderGroupLabel = normalizedOptions.metaLabelRenderer;
      else if ('regionLabelRenderer' in normalizedOptions) normalizedOptions.renderGroupLabel = normalizedOptions.regionLabelRenderer;
    }
    if (!('renderSubgroupLabel' in normalizedOptions)) {
      if ('labelRenderer' in normalizedOptions) normalizedOptions.renderSubgroupLabel = normalizedOptions.labelRenderer;
      else if ('bigClusterLabelRenderer' in normalizedOptions) normalizedOptions.renderSubgroupLabel = normalizedOptions.bigClusterLabelRenderer;
    }
    // Legacy unified renderLabel → fans out to both depth renderers (canonical/legacy options take precedence if set)
    if (normalizedOptions.renderLabel) {
      const r = normalizedOptions.renderLabel;
      if (!normalizedOptions.renderGroupLabel) normalizedOptions.renderGroupLabel = (d, html, ctx) => r(d, html, ctx);
      if (!normalizedOptions.renderSubgroupLabel) normalizedOptions.renderSubgroupLabel = (d, html, ctx) => r(d, html, ctx);
    }

    this.params = { ...VoronoiTreemap.DEFAULT_OPTIONS, ...normalizedOptions };

    // Normalize field names: custom levels/value -> standard metaLabel/label/text/bubbleSize
    const levels = this.params.levels;
    const valueField = this.params.value;
    const stdLevels = ['metaLabel', 'label', 'text'];
    const customLevels = !levels.every((f, i) => f === stdLevels[i]);
    const customValue = valueField !== 'bubbleSize';

    this.data = data.map(d => {
      const normalized = { ...d };
      // Legacy field name aliases
      if (!('metaLabel' in normalized) && 'region' in normalized) normalized.metaLabel = normalized.region;
      if (!('label' in normalized) && 'bigClusterLabel' in normalized) normalized.label = normalized.bigClusterLabel;
      if (!('text' in normalized) && 'clusterLabel' in normalized) normalized.text = normalized.clusterLabel;
      // Custom levels mapping
      if (customLevels) {
        if (levels[0] !== undefined) normalized.metaLabel = d[levels[0]];
        if (levels[1] !== undefined) normalized.label = d[levels[1]];
        if (levels[2] !== undefined) normalized.text = d[levels[2]];
      }
      if (customValue) normalized.bubbleSize = d[valueField];
      return normalized;
    });

    // Built-in sentiment colormap: `sentiment: 'fieldName'` or { field, domain: [lo, hi] }.
    // Auto-wires colorFunc (leaf cells) + keyColors (regions) from the diverging
    // pastel palette so a rating/sentiment field looks good with no extra code.
    // Explicit colorFunc / keyColors always take precedence.
    if (this.params.sentiment) {
      const sc = VoronoiTreemapHelpers.buildSentimentColoring(
        this.data,
        this.params.sentiment
      );
      if (!this.params.colorFunc) this.params.colorFunc = sc.colorFunc;
      if (!this.params.keyColors || this.params.keyColors.length === 0)
        this.params.keyColors = sc.keyColors;
    }

    // When colorFunc drives leaf colors but the user gave no palette/keyColors,
    // default the depth-1 (metaLabel) palette to a single neutral color so region
    // outlines and labels don't show clashing rainbow colors. Override with
    // `colors` / `keyColors` to restore per-region coloring.
    if (
      this.params.colorFunc &&
      !("colors" in normalizedOptions) &&
      (!this.params.keyColors || this.params.keyColors.length === 0)
    ) {
      this.params.colors = ["#444"];
    }

    this._setupSVG();
    this._prepareData();
    this._setupGroups();
    this._drawTitleAndCaption();
    this._createRegionColorScale();
    this._computeLayout();
    this._drawCells();
    this._drawCellImages();
    this._drawLabels();
    this._buildLabelCache();
    this._applyPostEffects();
    this._applyLabelMode();
    this._setupZoom();

    return this.svg.node();
  }

  /**
   * Update chart (data only)
   * @param {Object[]} newData - New data
   * @returns {SVGSVGElement}
   */
  update(newData) {
    return this.render(newData, this.params);
  }

  // === 1. Initial Setup Methods ===

  _setupSVG() {
    // Drawing coordinate space = params.width × params.height (the viewBox).
    // Display size is left to CSS: fills the container width, scales down on
    // mobile, and never grows past the native drawing size on large screens.
    // No JS scale math — the browser scales fonts/strokes/cells via the viewBox.
    this.svg = d3
      .create("svg")
      .attr("viewBox", `0 0 ${this.params.width} ${this.params.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "auto")
      .style("max-width", `${this.params.width}px`);

    // Inject bubble styles (includes :hover rules)
    this.svg.append("style").text(getBubbleStyles());

    this.svg
      .append("rect")
      .attr("width", "100%")
      .attr("height", "100%")
      .style("fill", "transparent");

    const innerWidth = this.params.width - this.margin.left - this.margin.right;
    const innerHeight = this.params.height - this.margin.top - this.margin.bottom;
    this.width = innerWidth * Math.sqrt(this.params.pieSize);
    this.height = this.width * (innerHeight / innerWidth);
  }

  _prepareData() {
    // Use external nestingForVoronoi function
    const nested = nestingForVoronoi(
      this.data,
      "label",
      "text"
    );

    this.hierarchy = d3.hierarchy(nested, (d) => d.values).sum((d) => d.size);

    this.totalValue = this.hierarchy.value;
  }

  _setupGroups() {
    // outer wrapper: applies static margin offset (never touched by zoom)
    this.chartGroup = this.svg
      .append("g")
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    // inner wrapper: applies dynamic zoom transform
    this.zoomGroup = this.chartGroup.append("g").attr("class", "zoom");

    this.voronoiGroup = this.zoomGroup.append("g").attr("class", "cell");
    this.labelsGroup = this.zoomGroup.append("g").attr("class", "labels");
    this.popLabelsGroup = this.zoomGroup.append("g").attr("class", "pop");
    this.bigLabelsGroup = this.zoomGroup.append("g").attr("class", "label1");
    this.percentLabelsGroup = this.zoomGroup
      .append("g")
      .attr("class", "percent");
    this.regionLabelsGroup = this.zoomGroup
      .append("g")
      .attr("class", "region");
  }

  _drawTitleAndCaption() {
    this.svg
      .append("g")
      .append("text")
      .attr("class", "title")
      .attr("text-anchor", "middle")
      .attr("font-size", "22")
      .attr("font-weight", "600")
      .attr(
        "transform",
        `translate(${this.margin.left + this.width / 2},${
          this.margin.top - 15
        })`
      )
      .html(this.params.maptitle);

    this.svg
      .append("g")
      .append("text")
      .attr("class", "caption")
      .attr("text-anchor", "middle")
      .attr("font-size", "15")
      .attr("font-weight", "400")
      .attr("fill", "#888")
      .attr(
        "transform",
        `translate(${this.margin.left + this.width / 2},${
          this.margin.top + this.height + 30
        })`
      )
      .html(this.params.caption);
  }

  _createRegionColorScale() {
    // Calculate total size (bubbleSize) per metaLabel
    const regionSizes = d3.rollup(
      this.data,
      (v) => d3.sum(v, (d) => parseFloat(d.bubbleSize) || 1),
      (d) => d.metaLabel
    );

    // Sort metaLabels by size (descending - largest first)
    const sortedRegions = Array.from(regionSizes.entries())
      .sort((a, b) => b[1] - a[1]) // b[1] - a[1]: descending order
      .map((d) => d[0]); // Extract metaLabel names

    const { keyColors, colors: paletteColors } = this.params;
    const customColorMap = new Map(keyColors.map((d) => [d.key, d.color]));

    let colorMapping = {};

    // Assign colors to sorted regions (largest region gets first color)
    sortedRegions.forEach((key, i) => {
      colorMapping[key] = paletteColors[i % paletteColors.length];
    });

    // Override with custom region colors if specified
    customColorMap.forEach((color, key) => {
      colorMapping[key] = color;
    });

    this.regionColor = d3
      .scaleOrdinal()
      .domain(Object.keys(colorMapping))
      .range(Object.values(colorMapping));
  }

  // === 2. Layout Calculation Methods ===

  _computeLayout() {
    const ellipse = d3.range(100).map((i) => {
      let x = Math.cos((i / 50) * Math.PI);
      const max = 0.92;
      if (x > max) x = max + (x - max) * 0.5;
      if (x < -max) x = -max + (x + max) * 0.5;
      x = x / 0.94;
      const y = Math.sin((i / 25) * Math.PI);
      return [
        (this.width * (1 + 0.99 * x)) / 2,
        (this.height * (1 + 0.99 * y)) / 2
      ];
    });

    const hasCustomPositions =
      Array.isArray(this.params.positions) &&
      this.params.positions.length > 0;

    if (hasCustomPositions || this.params.adaptiveIterations) {
      const mergedPositions = hasCustomPositions
        ? this._normalizePositions(this.params.positions)
        : [];
      const seed = d3.seedrandom(this.params.seedRandom);
      VoronoiTreemapHelpers.createCustomVoronoiAlgorithm(
        this,
        this.params.debug
      )
        .size([this.width, this.height])
        .clip(ellipse)
        .prng(seed)
        .adaptiveIterations(!!this.params.adaptiveIterations)
        .initialPositions(mergedPositions)(this.hierarchy);
    } else {
      // Default: single voronoi run with fixed seed
      const seed = d3.seedrandom(this.params.seedRandom);
      d3.voronoiTreemap().prng(seed).clip(ellipse)(this.hierarchy);
    }

    VoronoiTreemapHelpers.colorHierarchy(this, this.hierarchy);

    this.allNodes = this.hierarchy
      .descendants()
      .sort((a, b) => b.depth - a.depth)
      .map((d, i) => Object.assign({}, d, { id: i }));

    // Extract and provide cell colors if callback is provided
    if (this.params.getCellColors) {
      const cellColors = this._extractCellColors();
      this.params.getCellColors(cellColors);
    }
  }

  /**
   * Extract cell colors from hierarchy nodes
   * @returns {Array} Array of {metaLabel, metaColor, label, color} objects sorted by metaLabel size
   * @private
   */
  _extractCellColors() {
    const cellColors = [];

    // Get depth 1 nodes sorted by value (size) descending for ordering
    const depth1Nodes = this.hierarchy.descendants()
      .filter(n => n.depth === 1)
      .sort((a, b) => b.value - a.value);

    // Create order map for sorting
    const regionOrder = new Map();
    depth1Nodes.forEach((node, index) => {
      regionOrder.set(node.data.key, index);
    });

    this.hierarchy.descendants().forEach(node => {
      // Process depth 2 nodes (label level)
      if (node.depth === 2) {
        // depth 1 = metaLabel (parent), depth 2 = label (current node)
        const depth1Node = node.parent; // depth 1 ancestor

        cellColors.push({
          metaLabel: depth1Node?.data?.key,   // new
          bigLabel: depth1Node?.data?.key,     // legacy
          metaColor: depth1Node?.color,        // new
          bigColor: depth1Node?.color,         // legacy
          label: node.data.key,
          color: node.color
        });
      }
    });

    // Sort by metaLabel size (largest first, following colormap order)
    cellColors.sort((a, b) => {
      const orderA = regionOrder.get(a.metaLabel) ?? Infinity;
      const orderB = regionOrder.get(b.metaLabel) ?? Infinity;
      return orderA - orderB;
    });

    return cellColors;
  }

  _normalizePositions(regionPositions) {
    const result = [];
    const hierarchyNodes = this.hierarchy.descendants();

    // Helper: Add jitter to duplicate positions to prevent voronoi algorithm failure
    const addJitterForDuplicates = (positions, jitterAmount = 0.02) => {
      const positionMap = new Map(); // key: "x,y" -> count

      positions.forEach((pos) => {
        const key = `${pos.x.toFixed(6)},${pos.y.toFixed(6)}`;
        const count = positionMap.get(key) || 0;

        if (count > 0) {
          // Add jitter for duplicate positions (spread in a circle)
          const angle = (count * 2.4) % (2 * Math.PI); // Golden angle for even distribution
          const jitter = jitterAmount * Math.sqrt(count);
          pos.x = Math.max(0.05, Math.min(0.95, pos.x + jitter * Math.cos(angle)));
          pos.y = Math.max(0.05, Math.min(0.95, pos.y + jitter * Math.sin(angle)));
        }

        positionMap.set(key, count + 1);
      });

      return positions;
    };

    // depth 1: Normalize based on overall extent
    const depth1 = regionPositions.filter((p) => p.depth === 1);
    if (depth1.length > 0) {
      const xExtent = d3.extent(depth1, (p) => p.x);
      const yExtent = d3.extent(depth1, (p) => p.y);

      const xScale =
        xExtent[0] === xExtent[1]
          ? () => 0.5
          : d3.scaleLinear().domain(xExtent).range([0.15, 0.85]);
      const yScale =
        yExtent[0] === yExtent[1]
          ? () => 0.5
          : d3.scaleLinear().domain(yExtent).range([0.15, 0.85]);

      const depth1Normalized = depth1.map((pos) => ({
        ...pos,
        x: xScale(pos.x),
        y: yScale(pos.y)
      }));

      // Add jitter for any duplicate positions
      addJitterForDuplicates(depth1Normalized);
      result.push(...depth1Normalized);
    }

    // depth 2, 3: Find parent in hierarchy and normalize among siblings
    [2, 3].forEach((depth) => {
      const depthPositions = regionPositions.filter((p) => p.depth === depth);
      if (depthPositions.length === 0) return;

      // Find parent groups for nodes at this depth
      const nodesAtDepth = hierarchyNodes.filter((n) => n.depth === depth);
      const byParent = d3.group(nodesAtDepth, (n) => n.parent?.data?.key);

      byParent.forEach((siblings, parentKey) => {
        // Find regionPositions for these siblings
        const siblingKeys = new Set(siblings.map((s) => s.data.key));
        const siblingPositions = depthPositions.filter((p) =>
          siblingKeys.has(p.key)
        );

        if (siblingPositions.length === 0) return;

        const xExtent = d3.extent(siblingPositions, (p) => p.x);
        const yExtent = d3.extent(siblingPositions, (p) => p.y);

        const xScale =
          xExtent[0] === xExtent[1]
            ? () => 0.5
            : d3.scaleLinear().domain(xExtent).range([0.15, 0.85]);
        const yScale =
          yExtent[0] === yExtent[1]
            ? () => 0.5
            : d3.scaleLinear().domain(yExtent).range([0.15, 0.85]);

        const siblingNormalized = siblingPositions.map((pos) => ({
          ...pos,
          x: xScale(pos.x),
          y: yScale(pos.y)
        }));

        // Add jitter for any duplicate positions within siblings
        addJitterForDuplicates(siblingNormalized);
        result.push(...siblingNormalized);
      });
    });

    return result;
  }

  // === 3. Visualization Element Drawing Methods ===

  _drawCells() {
    const { clickFunc } = this.params;
    const self = this;

    this.voronoiGroup
      .selectAll("path")
      .data(this.allNodes)
      .enter()
      .append("path")
      .attr("d", (d) => "M" + d.polygon.join("L") + "Z")
      .style("fill", (d) => d.color ?? d.parent.color)
      .attr("class", (d) => {
        const areaClass = d.depth === 1 ? "metaLabelArea" : d.depth === 2 ? "labelArea" : d.depth === 3 ? "textArea" : "rootArea";
        return `${areaClass} area-${d.id}`;
      })
      .style("fill-opacity", (d) => (d.depth === 3 ? 1 : 0))
      .attr("pointer-events", (d) => (d.depth === 3 ? "all" : "none"))
      .on("click", function (e, d) {
        let area = self.voronoiGroup.select(`.area-${d.id}`);
        const clicked = area.attr("class").match("clicked");
        self.voronoiGroup.select(`.clicked`).classed("clicked", false);
        area.classed("clicked", !clicked);
        clickFunc(clicked ? "" : { ...d.data, event: e, d, clickArea: area });
      })
      .on("mouseenter", function (e, d) {
        // Label visibility - use cached lookups (O(1))
        const label1 = self._bigClusterLabelCache?.get(d.data.data.label);
        if (label1) label1.node().style.opacity = 1;

        const label = self._clusterLabelCache?.get(d.data.data.text);
        if (label) label.node().style.opacity = 1;
        // Highlight is handled by CSS :hover - no JS needed
      })
      .on("mouseleave", function (e, d) {
        // Label visibility - use cached lookups (O(1))
        const ratioLimit = self.params.ratioLimit;

        const label1 = self._bigClusterLabelCache?.get(d.data.data.label);
        if (label1) {
          label1.node().style.opacity = label1._cachedRatio >= ratioLimit ? 1 : 0;
        }

        const label = self._clusterLabelCache?.get(d.data.data.text);
        if (label) {
          label.node().style.opacity = label._cachedRatio >= ratioLimit ? 1 : 0;
        }
        // Highlight is handled by CSS :hover - no JS needed
      });
  }

  _drawCellImages() {
    const { cellImage } = this.params;
    if (!cellImage) return;

    const depth3 = this.allNodes.filter((d) => d.depth === 3);
    if (!depth3.length) return;

    let defs = this.svg.select("defs");
    if (defs.empty()) defs = this.svg.insert("defs", ":first-child");

    depth3.forEach((d) => {
      const imgOpts = cellImage(d.data.data ?? d.data);
      if (!imgOpts || !imgOpts.url) return;

      const { url, mode = "fill", opacity = 1, colorMode = "original" } = imgOpts;
      const id = `cell-img-${d.id}`.replace(/[^a-zA-Z0-9-_]/g, "_");
      const polygon = d.polygon;
      const xs = polygon.map((p) => p[0]);
      const ys = polygon.map((p) => p[1]);
      const bx = Math.min(...xs), by = Math.min(...ys);
      const bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by;

      const clipId = `${id}-clip`;
      const clip = defs.append("clipPath").attr("id", clipId);
      clip.append("path").attr("d", "M" + polygon.join("L") + "Z");

      let filterAttr = null;
      if (colorMode === "tint") {
        const filterId = `${id}-tint`;
        const filter = defs.append("filter")
          .attr("id", filterId)
          .attr("x", "0%").attr("y", "0%")
          .attr("width", "100%").attr("height", "100%")
          .attr("color-interpolation-filters", "sRGB");
        filter.append("feColorMatrix").attr("type", "saturate").attr("values", "0").attr("result", "gray");
        filter.append("feFlood").attr("flood-color", d.color).attr("flood-opacity", "1").attr("result", "color");
        filter.append("feBlend").attr("in", "color").attr("in2", "gray").attr("mode", "multiply").attr("result", "tinted");
        filter.append("feComposite").attr("in", "tinted").attr("in2", "SourceGraphic").attr("operator", "in");
        filterAttr = `url(#${filterId})`;
      }

      let imgX = bx, imgY = by, imgW = bw, imgH = bh;
      if (mode === "fit") {
        const side = Math.min(bw, bh) * 0.8;
        imgX = bx + (bw - side) / 2;
        imgY = by + (bh - side) / 2;
        imgW = side; imgH = side;
      }

      const imgEl = this.voronoiGroup.append("image")
        .attr("href", url)
        .attr("x", imgX).attr("y", imgY)
        .attr("width", imgW).attr("height", imgH)
        .attr("preserveAspectRatio", mode === "fit" ? "xMidYMid meet" : "xMidYMid slice")
        .attr("clip-path", `url(#${clipId})`)
        .attr("opacity", opacity)
        .attr("pointer-events", "none");

      if (filterAttr) imgEl.attr("filter", filterAttr);
    });
  }

  _drawLabels() {
    this._drawRegionLabels();
    this._drawBigClusterLabels();
    this._drawPercentLabels();
    this._drawSectorLabels();
    this._drawPopLabels();
  }

  _buildLabelCache() {
    // Build lookup maps for fast label access during hover events
    this._bigClusterLabelCache = new Map();
    this._clusterLabelCache = new Map();

    // Cache bigCluster labels with their ratio values
    this.bigLabelsGroup.selectAll("[data-bigCluster]").nodes().forEach((node) => {
      const key = node.getAttribute("data-bigCluster");
      if (key) {
        const selection = d3.select(node);
        selection._cachedRatio = parseFloat(node.getAttribute("data-ratio")) || 0;
        this._bigClusterLabelCache.set(key, selection);
      }
    });

    // Cache cluster labels with their ratio values
    this.labelsGroup.selectAll("[data-cluster]").nodes().forEach((node) => {
      const key = node.getAttribute("data-cluster");
      if (key) {
        const selection = d3.select(node);
        selection._cachedRatio = parseFloat(node.getAttribute("data-ratio")) || 0;
        this._clusterLabelCache.set(key, selection);
      }
    });
  }

  _drawRegionLabels() {
    const { showMetaLabel, renderGroupLabel } = this.params;
    const self = this;

    const regionNodes = this.allNodes.filter((d) => d.depth === 1);

    // If custom renderer exists, use foreignObject
    if (renderGroupLabel) {
      this.regionLabelsGroup
        .selectAll("foreignObject")
        .data(regionNodes)
        .enter()
        .append("foreignObject")
        .attr("class", "region-label-foreign")
        .attr("data-region", (d) => d.data.key)
        .attr("width", (d) => {
          const bounds = VoronoiTreemapHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return width * 0.6;
        })
        .attr("height", (d) =>
          VoronoiTreemapHelpers.estimateLabelHeight(this, d, 1.3)
        )
        .attr("x", (d) => {
          if (!d.polygon?.site) return 0;
          const bounds = VoronoiTreemapHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return d.polygon.site.x - (width * 0.6) / 2;
        })
        .attr(
          "y",
          (d) => {
            if (!d.polygon?.site) return 0;
            return d.polygon.site.y -
              VoronoiTreemapHelpers.estimateLabelHeight(this, d, 1.3) * 0.4 +
              VoronoiTreemapHelpers.getLabelHeightOffset(this, d);
          }
        )
        .style("opacity", showMetaLabel ? 1 : 0)
        .style("pointer-events", "none")
        .style("overflow", "visible")
        .append("xhtml:div")
        .style("width", "100%")
        .style("height", "100%")
        .style("display", "flex")
        .style("align-items", "center")
        .style("justify-content", "center")
        .html((d) => {
          const defaultHtml = VoronoiTreemapHelpers.multiline(d.data.key);
          const context = VoronoiTreemapHelpers.createLabelContext(this, d, 1);
          return renderGroupLabel(d, defaultHtml, context);
        });
    } else {
      // Default text rendering
      this.regionLabelsGroup
        .selectAll("text")
        .data(regionNodes)
        .enter()
        .append("text")
        .attr("class", "region")
        .attr("text-anchor", "start")
        .attr("ratio", (d) => d.value / d.parent.value)
        .style(
          "font-size",
          (d) =>
            VoronoiTreemapHelpers.fontScale(this.hierarchy, d) * 1.15 + "em"
        )
        .style("fill-opacity", showMetaLabel ? 1 : 0)
        .style("stroke-opacity", showMetaLabel ? 0.85 : 0)
        .style(
          "stroke",
          (d) => `${VoronoiTreemapHelpers.getHSLColor(d.color, 0, -0.05, -0.2)}`
        )
        .attr("paint-order", "stroke")
        .attr(
          "transform",
          (d) => {
            if (!d.polygon?.site) return `translate(0,0)`;
            return `translate(${[
              d.polygon.site.x,
              d.polygon.site.y +
                VoronoiTreemapHelpers.getLabelHeightOffset(this, d)
            ]})`;
          }
        )
        .html((d) => VoronoiTreemapHelpers.multiline(d.data.key));
    }
  }

  _drawBigClusterLabels() {
    const { ratioLimit, renderSubgroupLabel } = this.params;
    const self = this;

    const bigClusterNodes = this.allNodes.filter((d) => d.depth === 2);

    // If custom renderer exists, use foreignObject
    if (renderSubgroupLabel) {
      this.bigLabelsGroup
        .selectAll("foreignObject")
        .data(bigClusterNodes)
        .enter()
        .append("foreignObject")
        .attr("class", "bigcluster-label-foreign")
        .attr("data-bigCluster", (d) => d.data.key)
        .attr("data-value", (d) => d.value)
        .attr("data-ratio", (d) => d.value / this.totalValue)

        .attr("width", (d) => {
          const bounds = VoronoiTreemapHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return width * 0.6;
        })
        .attr("height", (d) =>
          VoronoiTreemapHelpers.estimateLabelHeight(this, d, 1.2)
        )
        .attr("x", (d) => {
          if (!d.polygon?.site) return 0;
          const bounds = VoronoiTreemapHelpers.getPolygonBounds(d.polygon);
          const width = bounds.maxX - bounds.minX;
          return d.polygon.site.x - (width * 0.6) / 2;
        })
        .attr(
          "y",
          (d) => {
            if (!d.polygon?.site) return 0;
            return d.polygon.site.y -
              VoronoiTreemapHelpers.estimateLabelHeight(this, d, 1.2) * 0.45 +
              VoronoiTreemapHelpers.getLabelHeightOffset(this, d);
          }
        )
        .attr("opacity", (d) =>
          d.value / this.totalValue >= ratioLimit ? 1 : 0
        )
        .style("pointer-events", "none")
        .style("overflow", "visible")
        .append("xhtml:div")
        .style("width", "100%")
        .style("height", "100%")
        .style("display", "flex")
        .style("align-items", "center")
        .style("justify-content", "center")
        .html((d) => {
          const defaultHtml = VoronoiTreemapHelpers.multiline(d.data.key);
          const context = VoronoiTreemapHelpers.createLabelContext(this, d, 2);
          return renderSubgroupLabel(d, defaultHtml, context);
        });
    } else {
      // Default text rendering
      this.bigLabelsGroup
        .selectAll("text")
        .data(bigClusterNodes)
        .enter()
        .append("text")
        .attr("class", "label-item")
        .attr("data-bigCluster", (d) => d.data.key)
        .attr("text-anchor", "start")
        .attr("data-value", (d) => d.value)
        .attr("data-ratio", (d) => d.value / this.totalValue)
        .style(
          "font-size",
          (d) => VoronoiTreemapHelpers.fontScale(this.hierarchy, d) + "em"
        )
        .attr("paint-order", "stroke")
        .style("fill", (d) =>
          VoronoiTreemapHelpers.colorVar2(d.parent.color, 0, 0.2, -0.2)
        )
        .attr(
          "transform",
          (d) => {
            if (!d.polygon?.site) return `translate(0,0)`;
            return `translate(${[
              d.polygon.site.x,
              d.polygon.site.y +
                VoronoiTreemapHelpers.getLabelHeightOffset(this, d)
            ]})`;
          }
        )
        .html((d) => VoronoiTreemapHelpers.multiline(d.data.key))
        .attr("opacity", (d) =>
          d.value / this.totalValue >= ratioLimit ? 1 : 0
        );
    }
  }

  _drawPercentLabels() {
    const { showPercent } = this.params;
    const percent_label_depth =
      this.allNodes.filter((d) => d.depth === 1).length > 1 ? 1 : 2;

    this.percentLabelsGroup
      .selectAll("text")
      .data(this.allNodes.filter((d) => d.depth === percent_label_depth))
      .enter()
      .append("text")
      .attr("class", (d) => `percent-label percent label-${d.id}`)
      .attr("text-anchor", "middle")
      .style(
        "font-size",
        (d) => VoronoiTreemapHelpers.fontScale(this.hierarchy, d) * 0.8 + "em"
      )
      .attr(
        "transform",
        (d) => {
          if (!d.polygon?.site) return `translate(0,0)`;
          return `translate(${[
            d.polygon.site.x,
            d.polygon.site.y +
              VoronoiTreemapHelpers.fontScale(this.hierarchy, d) *
                8 *
                (VoronoiTreemapHelpers.multiline(d.data.key, true)[1] + 2.2)
          ]})`;
        }
      )
      .text((d) => " " + d3.format(".0%")(d.value / this.totalValue))
      .attr("opacity", (d) =>
        showPercent
          ? Math.round((d.value / this.totalValue) * 100) > 0
            ? 1
            : 0
          : 0
      );
  }

  _drawSectorLabels() {
    const { underLabel, ratioLimit } = this.params;

    this.labelsGroup
      .selectAll("text")
      .data(this.allNodes.filter((d) => d.depth === 3))
      .enter()
      .append("text")
      .attr("class", (d) => `text-item label-${d.id}`)
      .attr("data-cluster", (d) => d.data.key)
      .attr("data-full-text", (d) => d.data.key)
      .attr("data-font-em", (d) => VoronoiTreemapHelpers.fontScale2(this.hierarchy, d))
      .attr("data-cell-w", (d) => {
        const xs = d.polygon.map(p => p[0]);
        return Math.max(...xs) - Math.min(...xs);
      })
      .attr("data-cell-h", (d) => {
        const ys = d.polygon.map(p => p[1]);
        return Math.max(...ys) - Math.min(...ys);
      })
      .attr("data-value", (d) => d.value)
      .attr("data-ratio", (d) => d.value / this.totalValue)
      .attr("text-anchor", "start")
      .style(
        "font-size",
        (d) => VoronoiTreemapHelpers.fontScale2(this.hierarchy, d) + "em"
      )
      .style("fill", (d) =>
        VoronoiTreemapHelpers.getHSLColor(d.color, 0, -0.1, -0.3)
      )
      .attr("transform", (d) => {
        if (!d.polygon?.site) return `translate(0,0)`;
        return underLabel
          ? `translate(${[
              d.polygon.site.x,
              d.polygon.site.y +
                VoronoiTreemapHelpers.fontScale1(
                  this.hierarchy,
                  d.data.data.label,
                  d.parent.value
                ) *
                  8 *
                  (VoronoiTreemapHelpers.multiline(
                    d.data.data.label,
                    true
                  )[1] +
                    0.5)
            ]})`
          : `translate(${VoronoiTreemapHelpers.getLabelPos(this, d)})`;
      })
      .html((d) => {
        const { text, charsPerLine } = VoronoiTreemapHelpers.truncateByCell(d.data.key, this.hierarchy, d);
        return VoronoiTreemapHelpers.multiline(text, false, charsPerLine, 1.4);
      })
      .attr("opacity", (d) => (d.value / this.totalValue > ratioLimit ? 1 : 0));
  }

  _drawPopLabels() {
    const { sizeLimit } = this.params;

    this.popLabelsGroup
      .selectAll("text")
      .data(this.allNodes.filter((d) => d.depth === 3))
      .enter()
      .append("text")
      .attr("class", (d) => `percent-label label-${d.id}`)
      .attr("text-anchor", "middle")
      .style(
        "font-size",
        (d) => VoronoiTreemapHelpers.fontScale2(this.hierarchy, d) * 0.8 + "em"
      )
      .attr(
        "data-pop",
        (d) => d.data.data.text ?? d.data.data.label
      )
      .attr(
        "transform",
        (d) => {
          if (!d.polygon?.site) return `translate(0,0)`;
          return `translate(${[
            d.polygon.site.x,
            d.polygon.site.y + VoronoiTreemapHelpers.varFontScale(this, d)
          ]})`;
        }
      )
      .text((d) => VoronoiTreemapHelpers.bigFormat(d.data.values[0].size))
      .attr("opacity", (d) => (d.value > sizeLimit ? 1 : 0));
  }

  // === 4. Post-processing and Effects Methods ===

  _applyPostEffects() {
    const { showMetaLabel, pebbleRound, pebbleWidth } = this.params;

    if (showMetaLabel) {
      this.labelAdjuster.adjust(this.svg.node(), { verticalSpacing: 0 });
    }

    this.pebbleRenderer.render(
      this.svg.node(),
      pebbleRound,
      pebbleWidth,
      VoronoiTreemapHelpers.colorVar.bind(VoronoiTreemapHelpers)
    );
  }

  _applyLabelMode(opacity) {
    const { labelMode } = this.params;
    const op = opacity !== undefined ? opacity
      : labelMode === 'show' ? 1
      : labelMode === 'faded' ? 0.6
      : 0;
    this.svg.selectAll('.labels tspan').style('opacity', op);
    this.svg.selectAll('.textArea').style('stroke-opacity', op);
  }

  _setupZoom() {
    const svg = this.svg;
    const zoomGroup = this.zoomGroup;
    const baseFontPx = 16;
    let lastK = 1;

    const zoom = d3.zoom()
      .scaleExtent([1, 12])
      .constrain((transform) => {
        const { k } = transform;
        if (k <= 1) return d3.zoomIdentity;
        // Allow panning so chart edges can reach viewport edges
        // (margin offsets are absorbed by chartGroup's static transform)
        const xMin = Math.min(0, this.params.width - this.margin.left - k * this.width);
        const yMin = Math.min(0, this.params.height - this.margin.top - k * this.height);
        const x = Math.max(xMin, Math.min(0, transform.x));
        const y = Math.max(yMin, Math.min(0, transform.y));
        return new transform.constructor(k, x, y);
      })
      .on('zoom', (event) => {
        const { x, y, k } = event.transform;
        zoomGroup.attr('transform', `translate(${x},${y}) scale(${k})`);

        // phase 1 (k≤growMax): text grows with zoom
        // phase 2 (k>growMax): text held at growMax screen size, char count grows instead
        const growMax = 2;
        const textScale = k <= growMax ? 1 : growMax / k;

        // labelMode: fade in labels/borders as zoom increases
        const modeOpacity = this.params.labelMode === 'show' ? 1
          : this.params.labelMode === 'faded' ? 0.6
          : 0;
        const zoomOpacity = modeOpacity + (1 - modeOpacity) * Math.min(1, k - 1);
        svg.selectAll('.labels tspan').style('opacity', zoomOpacity);
        svg.selectAll('.textArea').style('stroke-opacity', zoomOpacity);

        // keep stroke visually thin as zoom increases
        svg.selectAll('.textArea').style('stroke-width', `${0.5 / k}px`);
        svg.selectAll('.labelArea').style('stroke-width', `${0.7 / k}px`);
        svg.selectAll('.metaLabelArea').style('stroke-width', `${1.5 / k}px`);
        svg.selectAll('text.text-item, text.label-item, .region text, .label1 text, .percent text')
          .attr('transform', function() {
            const el = d3.select(this);
            const orig = el.attr('data-orig-transform') || el.attr('transform') || 'translate(0,0)';
            if (!el.attr('data-orig-transform')) el.attr('data-orig-transform', orig);
            return `${orig} scale(${textScale})`;
          });

        svg.selectAll('foreignObject.region-label-foreign, foreignObject.bigcluster-label-foreign')
          .each(function() {
            const fo = d3.select(this);
            if (!fo.attr('data-orig-x')) {
              fo.attr('data-orig-x', fo.attr('x') || '0')
                .attr('data-orig-y', fo.attr('y') || '0')
                .attr('data-orig-w', fo.attr('width') || '0')
                .attr('data-orig-h', fo.attr('height') || '0');
            }
            const cx = parseFloat(fo.attr('data-orig-x')) + parseFloat(fo.attr('data-orig-w')) / 2;
            const cy = parseFloat(fo.attr('data-orig-y')) + parseFloat(fo.attr('data-orig-h')) / 2;
            fo.attr('transform', `translate(${cx * (1 - textScale)},${cy * (1 - textScale)}) scale(${textScale})`);
          });

        // re-truncate text elements when zoom level changes by >20%
        if (Math.abs(k - lastK) / lastK > 0.2) {
          lastK = k;
          svg.selectAll('text.text-item[data-full-text]').each(function() {
            const el = d3.select(this);
            const fullText = el.attr('data-full-text');
            const cellW = parseFloat(el.attr('data-cell-w') || 0);
            const cellH = parseFloat(el.attr('data-cell-h') || 0);
            const fontEm = parseFloat(el.attr('data-font-em') || 0.7);
            const fontPx = fontEm * baseFontPx;
            const isLatin = !/[^\x00-\x7F]/.test(fullText);
            const charPx = isLatin ? fontPx * 0.55 : fontPx;
            // text-to-cell ratio scales by 1/textScale; identical to base in phase 1
            const charsPerLine = Math.max(1, Math.floor((cellW * 0.6) / (charPx * textScale)));
            const linesFit = Math.max(1, Math.floor(cellH / (fontPx * 1.4 * textScale)));
            const limit = Math.max(5, Math.min(fullText.length, charsPerLine * linesFit));
            const truncated = fullText.length <= limit ? fullText : fullText.slice(0, limit) + '…';
            el.html(VoronoiTreemapHelpers.multiline(truncated, false, charsPerLine, 1.4));
          });
        }
      });

    svg.call(zoom);

    // snap to origin when zoomed out to minimum
    zoom.on('end', (event) => {
      if (event.transform.k <= 1) {
        svg.transition().duration(120).call(zoom.transform, d3.zoomIdentity);
      }
    });

    // double-click to reset
    svg.on('dblclick.zoom', () => {
      svg.transition().duration(200).call(zoom.transform, d3.zoomIdentity);
    });
  }
}

export default VoronoiTreemap;
